/**
 * LOADING / STATE TRANSITIONS.
 *
 * `StateSwitch` crossfades between the fills of one box — skeleton, content,
 * empty, error — keyed by a state string. The outgoing fill finishes leaving
 * before the next arrives, so the two never stack, and because the parent box
 * is the same element throughout, nothing around it moves.
 *
 *   <StateSwitch state={isLoading ? 'loading' : error ? 'error' : rows.length ? 'ready' : 'empty'}>
 *     {isLoading ? <ListSkeleton/> : error ? <LoadError/> : …}
 *   </StateSwitch>
 *
 * `Staleable` is the refresh answer: content stays, dims, and comes back.
 * Never swap a table for a skeleton on a refetch — dim it.
 */
import React, { useEffect, useRef, useState } from 'react';
import { motion, PresenceContext } from 'framer-motion';
import { state as stateVariants, T } from './presets';
import { OPACITY } from './tokens';

interface StateSwitchProps {
  state: string;
  children: React.ReactNode;
  className?: string;
  /** Animate the very first fill too. Default off: the first paint is not a
   * transition, it is the screen. */
  initial?: boolean;
  /** Reserve a floor so a short empty state does not collapse the page. */
  minHeight?: number | string;
}

export const StateSwitch: React.FC<StateSwitchProps> = ({
  state,
  children,
  className,
  initial = false,
  minHeight,
}) => {
  /* ONE ELEMENT, NEVER UNMOUNTED — not an AnimatePresence swap. Measured on
     framer-motion 13.2: a motion element told to exit in the tick right after
     it mounted never finishes exiting, and in "wait" mode that blocks every
     fill that follows. The units screen hits exactly that timeline — a
     building change renders 'empty', the loader in that commit's own effect
     flips it to 'loading', and the box stayed on "No units yet" for good.

     So the crossfade is driven by hand: the current fill fades out, the
     content is swapped to the LATEST state on completion, and the new fill
     fades in. A state that changed again mid-fade is simply what gets shown at
     the swap — intermediate fills that lasted no time are skipped, which is
     what a crossfade should do with them anyway. `children` are captured per
     shown state so the fading-out fill never paints the next state's content. */
  const [shown, setShown] = useState(state);
  const [leaving, setLeaving] = useState(false);
  const latest = useRef(state);
  const lastChildren = useRef(children);

  latest.current = state;
  if (shown === state) {
    lastChildren.current = children;
  }

  useEffect(() => {
    if (state !== shown) {
      setLeaving(true);
    }
  }, [state, shown]);

  const settle = () => {
    if (leaving) {
      setShown(latest.current);
      setLeaving(false);
    }
  };

  return (
    <div
      className={className}
      style={minHeight ? { minHeight } : undefined}
      data-state-switch={state}
    >
      {/* OUT OF THE ROUTE'S PRESENCE. This fade is driven by hand and has no
          exit of its own, but a motion element inside a leaving route layer is
          still counted by that layer's AnimatePresence — and one whose
          `animate` flips mid-exit (a fill changing as the route leaves) never
          reports done in framer-motion 13.2. The desk layer in "wait" mode then
          held the old screen for good: picking a building on the menu's gate
          after a deep link left the URL on /visitor-log/expected and the Main
          Menu on screen (QA 2026-10-22). Isolated, the route layer exits as one
          piece and this box simply goes with it. Children get a null presence
          too, which is right: anything inside that wants its own exit has its
          own AnimatePresence. */}
      <PresenceContext.Provider value={null}>
        <motion.div
          variants={stateVariants}
          initial={initial ? 'hidden' : false}
          animate={leaving ? 'hidden' : 'visible'}
          onAnimationComplete={settle}
        >
          {shown === state ? children : lastChildren.current}
        </motion.div>
      </PresenceContext.Provider>
    </div>
  );
};

interface StaleableProps {
  stale: boolean;
  children: React.ReactNode;
  className?: string;
}

export const Staleable: React.FC<StaleableProps> = ({
  stale,
  children,
  className,
}) => (
  <motion.div
    className={className}
    initial={false}
    animate={{ opacity: stale ? OPACITY.refreshing : 1 }}
    transition={T.micro}
    aria-busy={stale || undefined}
  >
    {children}
  </motion.div>
);
